import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  LayoutGrid,
  List as ListIcon,
  CalendarDays,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Filter,
  Loader2,
} from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_ICON } from "../../styles/actionButtons";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import SelectMenu from "../../components/crm/SelectMenu";
import {
  COLUMNS,
  PRIORITY_META,
  deriveColumn,
  initialsOf,
  avatarColor,
  fmtDueShort,
  startOfToday,
} from "./tasksShared";

// How many cards each board column shows before "View more".
const PREVIEW_LIMIT = 10;

// A small Task/Event tag chip.
function TypeTag({ type }) {
  const isTask = type === "task";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
        isTask
          ? "bg-indigo-50 text-indigo-700 ring-indigo-200"
          : "bg-violet-50 text-violet-700 ring-violet-200"
      }`}
    >
      {isTask ? "Task" : "Event"}
    </span>
  );
}

function TaskCard({ card, onOpen }) {
  const pri = card.priority ? PRIORITY_META[card.priority] : null;
  return (
    <button
      type="button"
      onClick={() => onOpen(card)}
      className="w-full rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="min-w-0 flex-grow truncate text-[13px] font-semibold text-slate-900" title={card.title}>
          {card.title}
        </h4>
        <TypeTag type={card.type} />
      </div>
      {card.caseLabel && (
        <p className="mt-0.5 truncate text-[11px] text-slate-500" title={card.caseLabel}>
          {card.caseLabel}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        {pri ? (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${pri.pill}`}>
            {pri.label}
          </span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-[11px] text-slate-500">{fmtDueShort(card.dueDate)}</span>
          {card.assigneeName ? (
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ${avatarColor(
                card.assigneeName
              )}`}
              title={card.assigneeName}
            >
              {initialsOf(card.assigneeName)}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

export default function TasksBoard() {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const uid = globalUser?.uid;

  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board"); // board | list

  const [search, setSearch] = useState("");
  const [caseFilter, setCaseFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // all | task | event
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCols, setExpandedCols] = useState({}); // per-column "View more" toggle
  const filterRef = useRef(null);

  // Tasks (live)
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      collection(db, "lawyers", uid, "tasks"),
      (snap) => {
        setTasks(
          snap.docs.map((d) => {
            const t = d.data();
            return {
              id: d.id,
              type: "task",
              title: t.title || "Untitled task",
              caseLabel: t.linkedCaseName || t.linkedCaseNo || "",
              priority: t.priority || "medium",
              dueDate: t.dueDate || "",
              assigneeName: t.assigneeName || "",
              status: t.status || "todo",
              raw: t,
            };
          })
        );
        setLoading(false);
      },
      (e) => {
        console.error("tasks snapshot:", e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  // Events (one-time; hearings + manual events)
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "lawyers", uid, "events"));
        if (cancelled) return;
        setEvents(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => !e.isHidden)
            .map((e) => ({
              id: e.id,
              type: "event",
              title: e.title || "Event",
              caseLabel: e["Case No"] || "",
              priority: null,
              dueDate:
                typeof e.date === "string"
                  ? e.date
                  : e.date?.toDate
                  ? e.date.toDate().toISOString().slice(0, 10)
                  : "",
              assigneeName: "",
              status: e.status || "",
              raw: e,
            }))
        );
      } catch (e) {
        console.error("events fetch:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Close filter popover on outside click.
  useEffect(() => {
    if (!showFilters) return;
    const h = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilters(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showFilters]);

  const allItems = useMemo(() => [...tasks, ...events], [tasks, events]);

  const caseOptions = useMemo(() => {
    const s = new Set();
    allItems.forEach((i) => i.caseLabel && s.add(i.caseLabel));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [allItems]);

  const assigneeOptions = useMemo(() => {
    const s = new Set();
    tasks.forEach((t) => t.assigneeName && s.add(t.assigneeName));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((i) => {
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (priorityFilter !== "all" && i.priority !== priorityFilter) return false;
      if (caseFilter !== "all" && i.caseLabel !== caseFilter) return false;
      if (assigneeFilter !== "all" && i.assigneeName !== assigneeFilter) return false;
      if (q && !`${i.title} ${i.caseLabel}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allItems, search, typeFilter, priorityFilter, caseFilter, assigneeFilter]);

  const today = startOfToday();
  const byColumn = useMemo(() => {
    const map = { todo: [], inprogress: [], completed: [], overdue: [] };
    filtered.forEach((i) => {
      const col = deriveColumn(i, today);
      (map[col] || map.todo).push(i);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    );
    return map;
  }, [filtered, today]);

  const openCard = (card) => {
    if (card.type === "task") navigate(`/portal/task/${card.id}`);
    // events open in the existing calendar/event flow for now
  };

  const activeFilterCount =
    (typeFilter !== "all" ? 1 : 0) + (priorityFilter !== "all" ? 1 : 0);

  // max-w + truncate keeps long case-name options from blowing the select
  // out to full width (same fix as the PendingCases / AllCases toolbars).
  const selectCls =
    "max-w-[160px] truncate rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  const viewBtn = (key, Icon, label, onClick) => {
    const active = view === key;
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
          active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
        }`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-3">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-slate-500">Tasks &amp; Events</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-slate-500">Tasks</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-800">{view === "list" ? "List" : "Board"}</span>
        </div>

        {/* Title + actions */}
        <div className="mt-1.5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Tasks Board</h1>
            <p className="mt-0.5 text-xs text-slate-500">Manage and track all tasks in one place</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
              {viewBtn("board", LayoutGrid, "Board", () => setView("board"))}
              {viewBtn("list", ListIcon, "List", () => setView("list"))}
              <button
                type="button"
                onClick={() => navigate("/portal/calendar")}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                <CalendarDays className="h-3.5 w-3.5" /> Calendar
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate("/portal/add-task")}
              className={ACTION_BTN_GOLD}
            >
              <Plus className={ACTION_BTN_ICON} /> Add Task
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks..."
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
          </div>
          <div className="relative">
            <SelectMenu chevron={false} value={caseFilter} onChange={(e) => setCaseFilter(e.target.value)} className={`${selectCls} appearance-none pr-8`}>
              <option value="all">All Cases</option>
              {caseOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </SelectMenu>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          </div>
          <div className="relative">
            <SelectMenu chevron={false} value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className={`${selectCls} appearance-none pr-8`}>
              <option value="all">All Assignees</option>
              {assigneeOptions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </SelectMenu>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          </div>
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <Filter className="h-3.5 w-3.5 text-slate-400" /> Filters
              {activeFilterCount > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-yellow-500 px-1 text-[10px] font-bold text-gray-900">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilters && (
              <div className="absolute right-0 z-30 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
                <p className="mb-1.5 text-xs font-semibold text-slate-500">Type</p>
                <div className="flex gap-1">
                  {["all", "task", "event"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTypeFilter(t)}
                      className={`flex-1 rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${
                        typeFilter === t
                          ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <p className="mb-1.5 mt-3 text-xs font-semibold text-slate-500">Priority</p>
                <div className="flex flex-wrap gap-1">
                  {["all", "high", "medium", "low"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriorityFilter(p)}
                      className={`rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${
                        priorityFilter === p
                          ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : view === "board" ? (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {COLUMNS.map((col) => {
              const items = byColumn[col.key] || [];
              const isExpanded = !!expandedCols[col.key];
              const visibleItems = isExpanded ? items : items.slice(0, PREVIEW_LIMIT);
              const hiddenCount = items.length - visibleItems.length;
              return (
                <div key={col.key} className="flex flex-col rounded-2xl border border-slate-200 bg-white/60">
                  <div className={`flex items-center justify-between rounded-t-2xl px-3 py-2 ${col.tint}`}>
                    <h3 className="text-[13px] font-bold text-slate-800">
                      {col.label} <span className="text-slate-400">({items.length})</span>
                    </h3>
                  </div>
                  <div className="flex flex-col gap-2 p-2">
                    {visibleItems.map((card) => (
                      <TaskCard key={`${card.type}-${card.id}`} card={card} onOpen={openCard} />
                    ))}
                    {items.length > PREVIEW_LIMIT && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCols((prev) => ({ ...prev, [col.key]: !isExpanded }))
                        }
                        className="flex items-center justify-center gap-1 rounded-xl py-1.5 text-xs font-semibold text-yellow-700 transition-colors hover:text-yellow-800"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-3.5 w-3.5" /> View less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3.5 w-3.5" /> View more ({hiddenCount})
                          </>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/portal/add-task?status=${col.key}`)}
                      className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-2 text-xs font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Task
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Title</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Case</th>
                    <th className="px-3 py-2">Priority</th>
                    <th className="px-3 py-2">Assignee</th>
                    <th className="px-3 py-2">Due</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((i) => {
                    const col = COLUMNS.find((c) => c.key === deriveColumn(i, today));
                    const pri = i.priority ? PRIORITY_META[i.priority] : null;
                    return (
                      <tr
                        key={`${i.type}-${i.id}`}
                        onClick={() => openCard(i)}
                        className="cursor-pointer transition-colors hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 font-medium text-slate-900">{i.title}</td>
                        <td className="px-3 py-2"><TypeTag type={i.type} /></td>
                        <td className="px-3 py-2 text-slate-600">{i.caseLabel || "—"}</td>
                        <td className="px-3 py-2">
                          {pri ? (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${pri.pill}`}>
                              {pri.label}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{i.assigneeName || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDueShort(i.dueDate)}</td>
                        <td className="px-3 py-2 text-slate-600">{col?.label || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-500">No tasks or events match your filters.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
